/* ============================================
   COMMENTARY QUEUE - File de priorite
   ============================================
   Gere la file d'attente des commentaires
   avec le systeme de priorite P0/P1/P2.
   ============================================ */

/**
 * @typedef {Object} QueueItem
 * @property {string} eventType - Type d'evenement
 * @property {number} priority - 0=FLUX, 1=MOYEN, 2=MAJEUR
 * @property {string} team - 'HOME', 'AWAY', 'neutral'
 * @property {object} context - Contexte complet pour le LLM
 * @property {number} timestamp - Date.now() de la detection
 */

export class CommentaryQueue {
  constructor(options = {}) {
    this.maxQueueSize = options.maxQueueSize || 2
    this.minCooldownMs = options.minCooldownMs || 800
    this.idleTimerMs = options.idleTimerMs || 45000

    this.queue = []
    this.currentItem = null
    this.lastCommentaryEndTime = 0
    this.idleTimer = null
    this.playing = false

    // Callbacks
    this.onGenerate = options.onGenerate || (() => {})
    this.onCancel = options.onCancel || (() => {})
    this.onIdle = options.onIdle || (() => {})
    this.onLog = options.onLog || (() => {})
  }

  /**
   * Push a new event into the queue.
   * Handles priority-based interruption logic.
   *
   * @param {string} eventType
   * @param {number} priority - 0, 1, or 2
   * @param {string} team
   * @param {object} context
   */
  push(eventType, priority, team, context) {
    const item = {
      eventType,
      priority,
      team,
      context,
      timestamp: Date.now()
    }

    this._resetIdleTimer()

    // P2 (MAJEUR) — interrupt everything immediately
    if (priority === 2) {
      this.onLog(`[QUEUE] P2 event "${eventType}" — interrupting all`)
      if (this.playing) {
        this.onCancel()
      }
      this.queue = []
      this.currentItem = item
      this.playing = true
      this.onGenerate(item)
      return
    }

    // P1 (MOYEN)
    if (priority === 1) {
      if (!this.playing) {
        // Nothing playing — generate immediately
        this.onLog(`[QUEUE] P1 event "${eventType}" — generating now`)
        this.currentItem = item
        this.playing = true
        this.onGenerate(item)
        return
      }

      if (this.currentItem && this.currentItem.priority === 0) {
        // P0 in progress — interrupt it
        this.onLog(`[QUEUE] P1 event "${eventType}" — interrupting P0`)
        this.onCancel()
        this.currentItem = item
        this.onGenerate(item)
        return
      }

      // P1+ in progress — queue it (max 2)
      if (this.queue.length < this.maxQueueSize) {
        this.queue.push(item)
        this.onLog(`[QUEUE] P1 event "${eventType}" — queued (${this.queue.length}/${this.maxQueueSize})`)
      } else {
        this.onLog(`[QUEUE] P1 event "${eventType}" — dropped (queue full)`)
      }
      return
    }

    // P0 (FLUX) — only if nothing playing and cooldown passed
    if (priority === 0) {
      const sinceLast = Date.now() - this.lastCommentaryEndTime
      if (!this.playing && sinceLast >= this.minCooldownMs) {
        this.onLog(`[QUEUE] P0 event "${eventType}" — generating now`)
        this.currentItem = item
        this.playing = true
        this.onGenerate(item)
      } else {
        this.onLog(`[QUEUE] P0 event "${eventType}" — skipped (playing or cooldown)`)
      }
    }
  }

  /**
   * Called when the current commentary finishes playing.
   */
  onCommentaryEnd() {
    this.playing = false
    this.lastCommentaryEndTime = Date.now()
    this.currentItem = null

    // Process next item in queue
    if (this.queue.length > 0) {
      const next = this.queue.shift()
      this.onLog(`[QUEUE] Playing next from queue: "${next.eventType}" (${this.queue.length} remaining)`)
      this.currentItem = next
      this.playing = true
      this.onGenerate(next)
    } else {
      this._startIdleTimer()
    }
  }

  /**
   * Idle timer — triggers a context commentary when the match is quiet.
   */
  _startIdleTimer() {
    this._clearIdleTimer()
    this.idleTimer = setTimeout(() => {
      if (!this.playing) {
        this.onLog(`[QUEUE] Idle timer fired (${this.idleTimerMs}ms without commentary)`)
        this.onIdle()
      }
    }, this.idleTimerMs)
  }

  _resetIdleTimer() {
    this._clearIdleTimer()
    // Will be restarted when commentary ends
  }

  _clearIdleTimer() {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer)
      this.idleTimer = null
    }
  }

  /**
   * Get queue status for debugging.
   */
  getStatus() {
    return {
      playing: this.playing,
      currentEvent: this.currentItem?.eventType || null,
      currentPriority: this.currentItem?.priority ?? null,
      queueLength: this.queue.length,
      queueEvents: this.queue.map(i => i.eventType),
      sinceLastEnd: Date.now() - this.lastCommentaryEndTime
    }
  }

  destroy() {
    this._clearIdleTimer()
    this.queue = []
    this.currentItem = null
    this.playing = false
  }
}
